import {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  type ReactNode,
} from "react"

const DEFAULT_TITLE = "Focus"

// Document-title manager. We drive document.title IMPERATIVELY from React state
// and render NO <title> element (the static one in root.tsx was removed), so
// nothing in the React tree re-asserts the title and clobbers it — which is why
// the old `document.title = x` trick failed (React 19 owns/repaints <title>).
//
// Print handlers set a temporary title via this setter wrapped in flushSync;
// useLayoutEffect (NOT useEffect) is what makes flushSync commit document.title
// synchronously before window.print() / the macOS "Save as PDF" panel read it.
const DocumentTitleContext = createContext<(title: string | null) => void>(
  () => {},
)

export function DocumentTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null)
  useLayoutEffect(() => {
    document.title = title ?? DEFAULT_TITLE
  }, [title])
  return (
    <DocumentTitleContext.Provider value={setTitle}>
      {children}
    </DocumentTitleContext.Provider>
  )
}

/** Returns a setter for the document title. Pass a string to override (e.g. a
 *  print/"Save as PDF" filename) or null to restore the default. */
export function useSetDocumentTitle() {
  return useContext(DocumentTitleContext)
}
